import { useForm, getFormProps, getInputProps } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { z } from 'zod';
import {
	json,
	ActionFunctionArgs,
	LoaderFunctionArgs,
	redirect,
} from '@remix-run/node';
import {
	Form,
	useActionData,
	useLoaderData,
	useSearchParams,
} from '@remix-run/react';
import { InputField, ErrorList } from '#app/components/forms.tsx';
import Logo from '#/app/components/logo.tsx';
import { Button } from '#/app/components/ui/button.tsx';
import { getRandomBackground } from '#/app/utils/scraper.server.ts';
import { EmailSchema } from '#app/utils/validations';
import { requireAnonymous } from '#/app/utils/session.server.ts';
import { checkHoneypot } from '#/app/utils/honeypot.server.ts';
import { HoneypotInputs } from 'remix-utils/honeypot/react';
import { prisma } from '#/app/utils/db.server.ts';

const LoginFormSchema = z.object({
	email: EmailSchema,
	redirectTo: z.string().optional(),
});

export async function action({ request }: ActionFunctionArgs) {
	await requireAnonymous(request);
	const formData = await request.formData();
	checkHoneypot(formData);
	const submission = await parseWithZod(formData, {
		schema: intent =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data };

				if (!data.email.endsWith('@architectus.com.au')) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Invalid email',
					});
					return z.NEVER;
				}
				return { ...data };
			}),
		async: true,
	});
	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		);
	}

	return redirect(
		`/auth/authenticate?redirectTo=${submission.value.redirectTo}&login_hint=${submission.value.email}`,
	);
}

export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request);
	const background = await getRandomBackground();
	return json({ background });
}
export default function Login() {
	const actionData = useActionData<typeof action>();
	const data = useLoaderData<typeof loader>();
	const [searchParams] = useSearchParams();
	const redirectTo = searchParams.get('redirectTo') || '/';

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema });
		},
		shouldRevalidate: 'onBlur',
	});

	return (
		<div className="flex h-full flex-1">
			<div className="flex flex-1 flex-col overflow-hidden lg:max-w-xl">
				<div className="p-4 sm:p-6 lg:p-8 ">
					<Logo />
				</div>
				<div className="flex h-full flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24 ">
					<div className="mx-auto w-full max-w-sm pb-40 sm:pb-44 lg:w-96 lg:pb-56">
						<div>
							<h2 className="mt-8 text-center text-5xl font-bold leading-tight tracking-tight sm:text-start">
								Welcome
							</h2>
							<h3 className="text-center text-lg font-light text-muted-foreground sm:text-start">
								Please enter your details to log in
							</h3>
						</div>

						<div className="mt-8">
							<Form method="POST" {...getFormProps(form)}>
								<HoneypotInputs />
								<InputField
									labelProps={{ children: 'Email address' }}
									inputProps={{
										...getInputProps(fields.email, { type: 'email' }),
										className: 'mt-2',
										autoFocus: true,
										autoCapitalize: 'none',
										autoComplete: 'email',
										autoCorrect: 'off',
									}}
									errors={form.errors}
								/>
								<input
									{...getInputProps(fields.redirectTo, { type: 'hidden' })}
								/>

								<div>
									<Button type="submit" className="w-full">
										Continue
									</Button>
								</div>
							</Form>
						</div>
					</div>
				</div>
			</div>
			<div className="relative hidden w-0 flex-1 overflow-hidden lg:block">
				<img
					className="animate-zoom absolute inset-0 h-full w-full object-cover"
					src={data.background}
					alt="Architectus Project Image"
				/>
			</div>
		</div>
	);
}
